import { useState } from "react";
import { GripVertical, Package, Play, ArrowUp, ArrowDown, X, Clock, Check } from "lucide-react";

/** Liste ordonnée des séquences SCORM d'un module de cours (glisser-déposer, renommage, durée, aperçu, retrait). */
export function SequenceList({ sequences, onReorder, onRename, onDuration, onPreview, onRemove }) {
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);

  const move = (from, to) => {
    if (from === to || from == null || to == null) return;
    const a = [...sequences];
    const [item] = a.splice(from, 1);
    a.splice(to, 0, item);
    onReorder(a.map((s) => s.module_id));
  };

  if (!sequences.length) return null;
  return (
    <div className="space-y-1.5" data-testid="cm-sequence-list">
      {sequences.map((s, i) => (
        <div
          key={s.module_id}
          draggable
          onDragStart={() => setDrag(i)}
          onDragOver={(e) => { e.preventDefault(); setOver(i); }}
          onDragLeave={() => setOver(null)}
          onDrop={(e) => { e.preventDefault(); move(drag, i); setDrag(null); setOver(null); }}
          onDragEnd={() => { setDrag(null); setOver(null); }}
          className={`group flex items-center gap-2 bg-[#0b0811] border rounded-xl px-3 py-2.5 transition-colors ${over === i && drag !== i ? "border-[#f7c935]/60" : "border-white/5 hover:border-white/15"}`}
          data-testid={`cm-sequence-row-${s.module_id}`}
        >
          <GripVertical className="w-4 h-4 text-slate-600 cursor-grab shrink-0" />
          <span className="text-[10px] text-slate-500 w-5 shrink-0">{i + 1}.</span>
          <Package className="w-4 h-4 text-sky-400 shrink-0" />
          <InlineTitle value={s.title} onSave={(t) => onRename(s.module_id, t)} testId={`cm-seq-title-${s.module_id}`} />
          <span className="hidden md:inline text-[9px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 font-bold uppercase shrink-0">SCORM {s.scorm_version || ""}</span>
          <span className="hidden lg:inline text-[10px] text-slate-500 shrink-0">{s.scorm_file_count || 0} fichiers</span>
          <label className="hidden sm:flex items-center gap-1 text-[10px] text-slate-500 shrink-0" title="Durée (min)">
            <Clock className="w-3 h-3" />
            <input type="number" min="0" value={s.duration_min || 0} onChange={(e) => onDuration(s.module_id, parseInt(e.target.value || "0", 10))} className="w-12 bg-transparent border border-white/10 rounded px-1 py-0.5 text-[11px] text-white text-right" data-testid={`cm-seq-duration-${s.module_id}`} />
            <span>min</span>
          </label>
          <button type="button" onClick={() => onPreview(s)} className="p-1 rounded text-slate-400 hover:text-white hover:bg-white/10" title="Tester la séquence" data-testid={`cm-seq-preview-${s.module_id}`}><Play className="w-3.5 h-3.5" /></button>
          <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-20" data-testid={`cm-seq-up-${s.module_id}`}><ArrowUp className="w-3.5 h-3.5" /></button>
          <button type="button" onClick={() => move(i, i + 1)} disabled={i === sequences.length - 1} className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-20" data-testid={`cm-seq-down-${s.module_id}`}><ArrowDown className="w-3.5 h-3.5" /></button>
          <button type="button" onClick={() => onRemove(s)} className="p-1 rounded text-red-400/70 hover:text-red-400 hover:bg-red-500/10" title="Retirer la séquence" data-testid={`cm-seq-remove-${s.module_id}`}><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </div>
  );
}

function InlineTitle({ value, onSave, testId }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  const commit = () => { setEditing(false); const t = val.trim(); if (t && t !== value) onSave(t); else setVal(value || ""); };
  if (!editing) {
    return <button type="button" onClick={() => { setVal(value || ""); setEditing(true); }} className="flex-1 min-w-0 text-left text-sm text-white truncate hover:text-[#f7c935]" title="Renommer" data-testid={testId}>{value || "Séquence"}</button>;
  }
  return (
    <div className="flex-1 min-w-0 flex items-center gap-1">
      <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setVal(value || ""); setEditing(false); } }} className="flex-1 min-w-0 bg-[#14101c] border border-[#f7c935]/40 rounded px-2 py-0.5 text-sm text-white outline-none" data-testid={`${testId}-input`} />
      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={commit} className="text-emerald-400"><Check className="w-3.5 h-3.5" /></button>
    </div>
  );
}
